/*
 * Photos that did not send (owner, Oct 31: "when I'm adding a photo and it fails ... it's gone and I have to literally
 * take a picture again"). A photo that fails for a bad connection, a server hiccup or a sign in that ran out is kept on
 * this phone (IndexedDB, so closing the page keeps it too) and shows as Didn't send with Try again and Remove. Kept photos
 * send again by themselves when the phone is back online and when the page opens. A photo the server turns down for good
 * (not a photo, too big) is not kept: trying again would fail the same way.
 */
import { useSyncExternalStore } from "react";
import { api, ApiError, companyStore } from "../api/client";

export interface HeldPhoto {
  id: string;
  /** Where it shows: "visits:<ref>" on the office visit page, "crew:<ref>" on the crew's. */
  key: string;
  companyId: string;
  /** The address it posts to, as api() takes it. */
  path: string;
  body: { name: string; mime: string; dataBase64: string; kind?: string; room?: string | undefined };
  at: number;
  error: string;
  /** Sending again right now. */
  busy?: boolean;
}

const DB = "steward-photos";
const STORE = "held";
let held: HeldPhoto[] = [];
const listeners = new Set<() => void>();
const changed = () => {
  held = [...held];
  for (const l of listeners) l();
};
/** Run after a kept photo finally sends, so the page shows it (the caller refreshes its queries). */
const onSent = new Set<(p: HeldPhoto) => void>();

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" });
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
    } catch {
      // A private window can refuse storage: the photo is then kept until the page closes.
      resolve(null);
    }
  });
}

async function write(run: (s: IDBObjectStore) => void) {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, "readwrite");
    run(tx.objectStore(STORE));
    await new Promise<void>((done) => {
      tx.oncomplete = () => done();
      tx.onerror = () => done();
    });
  } catch {
    // Kept in memory anyway.
  } finally {
    db.close();
  }
}

let loaded: Promise<void> | null = null;
function load(): Promise<void> {
  loaded ??= (async () => {
    const db = await openDb();
    if (!db) return;
    try {
      const rows = await new Promise<HeldPhoto[]>((done) => {
        const r = db.transaction(STORE).objectStore(STORE).getAll();
        r.onsuccess = () => done(r.result as HeldPhoto[]);
        r.onerror = () => done([]);
      });
      const have = new Set(held.map((p) => p.id));
      held = [...held, ...rows.filter((p) => !have.has(p.id)).map((p) => ({ ...p, busy: false }))];
      changed();
    } catch {
      // Nothing kept.
    } finally {
      db.close();
    }
  })();
  return loaded;
}

/** Worth keeping and trying again: no connection, sign in ran out, too many at once, or the server had a problem. */
export function worthKeeping(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  return e.status === 0 || e.status === 401 || e.status === 408 || e.status === 429 || e.status >= 500;
}

const words = (e: unknown) =>
  e instanceof ApiError && e.status !== 0 ? e.message : "No connection. It sends again when you are back online.";

/** Keep a photo that did not send. */
export function holdPhoto(key: string, path: string, body: HeldPhoto["body"], e: unknown): void {
  const companyId = companyStore.get();
  if (!companyId) return;
  const p: HeldPhoto = { id: crypto.randomUUID(), key, companyId, path, body, at: Date.now(), error: words(e) };
  held = [...held, p];
  changed();
  void write((s) => s.put({ ...p, busy: false }));
}

export function dropPhoto(id: string): void {
  held = held.filter((p) => p.id !== id);
  changed();
  void write((s) => s.delete(id));
}

/** Send one kept photo again. True when it went. */
export async function retryPhoto(id: string): Promise<boolean> {
  const p = held.find((x) => x.id === id);
  if (!p || p.busy || p.companyId !== companyStore.get()) return false;
  p.busy = true;
  changed();
  try {
    await api("POST", p.path, p.body);
    dropPhoto(id);
    for (const f of onSent) f(p);
    return true;
  } catch (e) {
    // Turned down for good (the visit is gone, not a photo): stays until removed, with the reason.
    held = held.map((x) => (x.id === id ? { ...x, busy: false, error: words(e) } : x));
    changed();
    return false;
  }
}

/** Try every kept photo for the company in use, one after another. */
async function retryAll() {
  await load();
  for (const p of held.filter((x) => x.companyId === companyStore.get())) await retryPhoto(p.id);
}

let started = false;
function start() {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("online", () => void retryAll());
  void retryAll();
}

const subscribe = (l: () => void) => {
  start();
  listeners.add(l);
  return () => listeners.delete(l);
};

/** The photos kept for one place, oldest first. */
export function useHeldPhotos(key: string): HeldPhoto[] {
  const all = useSyncExternalStore(subscribe, () => held);
  return all.filter((p) => p.key === key && p.companyId === companyStore.get());
}

/** Called after a kept photo sends, so the page can refresh. Returns the undo. */
export function whenPhotoSent(f: (p: HeldPhoto) => void): () => void {
  onSent.add(f);
  return () => onSent.delete(f);
}
